/**
 * Research-profiles service — async "research a new HNW profile from a
 * name + optional context" jobs. Mirrors the split in search.js: this file
 * owns the fetch calls, adapters.js owns the raw-API-shape-to-UI-shape
 * transforms.
 */
import { apiFetch } from './client.js';
import {
  researchProfileFromApi,
  paginatedResearchFromApi,
  researchProfileShareFromApi,
  regenerationStatusFromApi,
} from './adapters.js';

/**
 * Page of ResearchProfileSummary rows — id/name/context/status/timestamps
 * only. The listing shape carries no result/citations/raw_result, so a row
 * from here can never back the dossier page; opening a profile always goes
 * through getResearchProfile (see routes/research-profiles.$id.jsx).
 */
export async function listResearchProfiles({ page = 1, size = 20, signal } = {}) {
  const payload = await apiFetch('/research-profiles', { params: { page, size }, signal });
  return paginatedResearchFromApi(payload);
}

/**
 * `mode` is ProfileBuilderMode on the backend ("simple" | "ledger", defaults
 * to "simple" server-side if omitted) — which research pipeline builds the
 * profile. Passed through as-is rather than defaulted here so a caller can
 * still omit it and rely on the backend default.
 */
export async function createResearchProfile({ name, context, mode, signal }) {
  const payload = await apiFetch('/research-profiles', {
    method: 'POST',
    body: { name, context: context || null, ...(mode ? { mode } : {}) },
    signal,
  });
  return researchProfileFromApi(payload);
}

export async function getResearchProfile(id, { signal } = {}) {
  const payload = await apiFetch(`/research-profiles/${encodeURIComponent(id)}`, { signal });
  return researchProfileFromApi(payload);
}

/**
 * Create a public, expiring share link for a research profile (owner-only —
 * the backend enforces this, a non-owner gets a 403). Defaults to 48h/2 days
 * since the backend's own default (24h) undershoots that. Returns the raw
 * ResearchProfileShare payload — payload.id is the opaque share token used to
 * build the public URL, payload.expires_at is the real expiry to display.
 */
export async function createResearchProfileShare(id, { expiresInHours = 48, signal } = {}) {
  return apiFetch(`/research-profiles/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    body: { expires_in_hours: expiresInHours },
    signal,
  });
}

/** Fetch a shared profile by its public token — no auth required. */
export async function getSharedResearchProfile(token, { signal } = {}) {
  const payload = await apiFetch(`/research-profiles/shared/${encodeURIComponent(token)}`, { signal });
  return researchProfileShareFromApi(payload);
}

/**
 * Overwrite one section of a COMPLETED research profile's result (owner-only
 * — the backend 404s for a non-owner/missing profile, 409s if it isn't
 * COMPLETED yet). `sectionData` must already match that section's shape
 * (object or array per ApiProfile's field for `sectionName`) — the backend
 * validates/normalizes it, clears that section's citations, and returns the
 * full updated ResearchProfile.
 */
export async function updateResearchProfileSection(id, sectionName, sectionData, { signal } = {}) {
  const payload = await apiFetch(
    `/research-profiles/${encodeURIComponent(id)}/sections/${encodeURIComponent(sectionName)}`,
    { method: 'PATCH', body: sectionData, signal },
  );
  return researchProfileFromApi(payload);
}

/**
 * Kick off a regeneration job for a COMPLETED research profile — either one
 * segment (`fieldName` = an ApiProfile field name, e.g. "professional_roles")
 * or the whole profile (`fieldName` omitted/null). Returns immediately (202);
 * the backend runs the job in the background. Segment runs for different
 * fields can be in flight at the same time, but a full run is exclusive —
 * poll getResearchProfileRegenerationStatus for the active list, then refetch
 * the profile (getResearchProfile) as each job drops off it.
 */
export async function regenerateResearchProfile(id, { fieldName = null, signal } = {}) {
  const payload = await apiFetch(`/research-profiles/${encodeURIComponent(id)}/regenerate`, {
    method: 'POST',
    body: { field_name: fieldName },
    signal,
  });
  return researchProfileFromApi(payload);
}

/** Poll target for an in-flight regeneration job — see regenerateResearchProfile. */
export async function getResearchProfileRegenerationStatus(id, { signal } = {}) {
  const payload = await apiFetch(`/research-profiles/${encodeURIComponent(id)}/regeneration`, { signal });
  return regenerationStatusFromApi(payload);
}

/**
 * Attach an extra source document to one section's segment — the file is
 * stripped/extracted server-side (AddSegmentSource) and echoed back as the
 * ResearchProfileSource that was recorded; it does NOT itself change
 * `result` (that only happens once the section is regenerated using it).
 * `url` is the source's attributed URL (e.g. where the uploaded file came
 * from), `file` is the actual File/Blob to upload.
 */
export async function addResearchProfileSectionSource(id, fieldName, { url, file, signal } = {}) {
  const form = new FormData();
  form.append('url', url);
  form.append('file', file);
  return apiFetch(
    `/research-profiles/${encodeURIComponent(id)}/sections/${encodeURIComponent(fieldName)}/sources`,
    { method: 'POST', body: form, signal },
  );
}
